import { exportRepoDocuments } from 'api/v1/export';
import getRepoDocuments from 'api/v2/repository/getRepoDocuments';
import { Button } from 'components/atoms/Button';
import { Modal, type ModalApi } from 'components/atoms/Modal';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import ExportIcon from 'components/atoms/SVG/sidebarIcons/ExportIcon.component';
import CustomLoader from 'components/molecules/CustomLoader.component';
import ModalHeading from 'components/molecules/ModalHeading';
import usePerformExport from 'hooks/DataExport/usePerformExport';
import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ExportedDocCountCacheKeys } from 'types/cacheKeys.types';
import showToast from 'utils/toast';

const ExportDocumentsContent: FC<{
  onExport: () => void;
  isLoading: boolean;
  filters: Record<string, any>;
}> = ({ onExport, isLoading, filters }) => {
  const { t } = useTranslation();

  const actualExportedDocAPI = useQuery(
    [ExportedDocCountCacheKeys.ActualExportedDocCount, filters],
    () => getRepoDocuments({ ...filters, page: 1, perPage: 1 }),
  );

  const appliedFiltersCount = Object.keys(filters).filter(
    (filterKey) => !!filters[filterKey],
  );

  if (actualExportedDocAPI.isLoading) return <CustomLoader />;

  const actualExportDocCount = (actualExportedDocAPI.data as any)?.paging
    ?.totalCount;

  return (
    <div>
      <div className="my-3">
        <SimpleAlertV2
          title={
            appliedFiltersCount.length === 1
              ? t('export.filter_alert.msg', {
                  filters: appliedFiltersCount.length,
                  docCount: actualExportDocCount,
                })
              : t('export.filters_alert.msg', {
                  filters: appliedFiltersCount.length,
                  docCount: actualExportDocCount,
                })
          }
          type="info"
        />

        {actualExportDocCount > 500 ? (
          <div className="mt-3">
            <SimpleAlertV2
              title={t('export.alert.max_doc_count')}
              type="error"
            />
          </div>
        ) : null}
      </div>

      <Button
        onClick={onExport}
        disabled={
          isLoading || actualExportDocCount > 500 || actualExportDocCount === 0
        }
        loading={isLoading}
        className="w-full mt-3"
      >
        <span className="first-letter:capitalize">{t('export')}</span>
      </Button>
    </div>
  );
};

const ExportDocuments: FC<{ filters: Record<string, any> }> = ({ filters }) => {
  const { t } = useTranslation();

  const modalRef = useRef<ModalApi>(null);

  const exportController = usePerformExport(
    () => {
      return exportRepoDocuments(filters, {
        modules: {
          repository_documents: { include: true },
        },
        sendEmail: false,
      });
    },
    () => {
      (modalRef.current as any)?.closeModal();

      return showToast(
        {
          title: t('export.success_msg'),
        },
        { type: 'success' },
      );
    },
  );

  return (
    <Modal
      name="documents repository export"
      apiRef={modalRef}
      trigger={(api) => (
        <Button
          structure="secondary"
          color="primary"
          className="first-letter:!capitalize !border-gray-200 mx-2"
          onClick={(e) => {
            e.stopPropagation();
            api.openModal();
          }}
          disabled={exportController.isLoading}
          loading={exportController.isLoading}
          icon={() => <ExportIcon className="w-5" />}
        >
          <span className="first-letter:capitalize">{t('export')}</span>
        </Button>
      )}
      render={() => (
        <div>
          <ModalHeading
            Icon={ExportIcon}
            color="primary"
            title="Export documents repository"
          />

          <ExportDocumentsContent
            onExport={() => exportController.mutate({})}
            isLoading={exportController.isLoading}
            filters={filters}
          />
        </div>
      )}
    />
  );
};

export default ExportDocuments;
